import { Link, NavLink, Outlet } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useCart } from '@/store/cart';
import { useFavourites } from '@/store/favourites';
import { ThemeToggle } from '@/components/ThemeToggle';
import BottomNav from '@/components/layout/BottomNav';
import synterraLogo from '@/assets/synterra-logo.png';

const announcements = [
  { icon: '🛡️', text: 'EVERY SELLER IS ID-VERIFIED · PAYMENTS SECURED BY STRIPE', code: null, codeLabel: false },
  { icon: '✨', text: 'NEW: AI LISTING TOOL - LIST 20 ITEMS IN 5 MINUTES', code: 'TRY IT →', codeLabel: false },
  { icon: '🏆', text: 'TOP SELLERS EARN GOLD BADGE · 3.4× MORE VISIBILITY', code: null, codeLabel: false },
];

interface MegaCat {
  label: string; slug: string; icon: string;
  subs: string[]; brands: string[]; featured?: string; img: string; blurb: string;
}
const mImg = (id: string) => `https://images.unsplash.com/photo-${id}?w=640&h=860&fit=crop&auto=format&q=80`;
const megaCategories: MegaCat[] = [
  { label: 'Women',       icon: '👗',  slug: 'womenswear',  img: mImg('1539109136881-3be0616acf4b'), blurb: 'Curated pre-loved womenswear from authenticated sellers.',  subs: ['Dresses', 'Tops & Blouses', 'Coats & Jackets', 'Knitwear', 'Trousers', 'Skirts', 'Activewear', 'Occasionwear'], brands: ['Max Mara', 'COS', 'Acne Studios', 'Sandro', 'Loewe'],      featured: 'New autumn arrivals' },
  { label: 'Men',         icon: '👔',  slug: 'menswear',    img: mImg('1521572163474-6864f9cf17ab'), blurb: 'Tailoring, outerwear and staples, second-hand not second-best.', subs: ['Shirts', 'Trousers & Chinos', 'Coats & Jackets', 'Knitwear', 'T-Shirts', 'Suits', 'Activewear', 'Accessories'], brands: ['Carhartt', 'Patagonia', 'Stone Island', 'Ralph Lauren', 'COS'], featured: 'Pre-loved tailoring' },
  { label: 'Kids',        icon: '🧸',  slug: 'kids',        img: mImg('1503944168849-8bf86cb31b6c'), blurb: 'Outgrown, not outworn - quality childrenswear for less.',   subs: ['Baby 0-2', 'Toddler 2-5', 'Girls 5-12', 'Boys 5-12', 'Teens', 'Shoes & Boots', 'School Uniform', 'Coats'], brands: ['Bonpoint', 'Mini Rodini', 'Jacadi', 'Caramel', 'Bobo Choses'], featured: 'Outgrown, not outworn' },
  { label: 'Traditional', icon: '🥻',  slug: 'traditional', img: mImg('1490481651871-ab68de25d43d'), blurb: 'Heritage attire and handcrafted fabrics with a story.',     subs: ['Agbada & Dashiki', 'Ankara Prints', 'Kaftans', 'Abayas & Thobes', 'Saris & Salwar', 'Yoruba Attire', 'Igbo Attire', 'Accessories'], brands: ['Vlisco', 'House of Deola', 'Ozwald Boateng', 'Lisa Folawiyo', 'Duro Olowu'], featured: 'Heritage fashion' },
  { label: 'Shoes',       icon: '👟',  slug: 'shoes',       img: mImg('1491553895911-0055eca6402d'),   blurb: 'Barely-worn soles - trainers to heels, all authenticated.', subs: ['Trainers', 'Boots', 'Heels', 'Loafers', 'Sandals', 'Formal', 'Platforms', 'Sliders'], brands: ['Nike', 'New Balance', 'Adidas', 'Birkenstock', 'Common Projects'], featured: 'Barely worn soles' },
  { label: 'Bags',        icon: '👜',  slug: 'bags',        img: mImg('1548036328-c9fa89d128fa'),   blurb: 'Investment pieces, verified for authenticity before sale.', subs: ['Totes', 'Crossbody', 'Shoulder Bags', 'Clutches', 'Backpacks', 'Mini Bags', 'Travel', 'Belt Bags'], brands: ['Mulberry', 'Loewe', 'Toteme', 'By Far', 'Bottega'],            featured: 'Investment pieces' },
  { label: 'Accessories', icon: '💍',  slug: 'accessories', img: mImg('1524592094714-0f0654e20314'), blurb: 'The finishing touches - jewellery, watches, scarves & more.', subs: ['Jewellery', 'Scarves & Wraps', 'Belts', 'Sunglasses', 'Hats & Caps', 'Wallets', 'Hair Accessories', 'Watches'], brands: ['Hermès', 'Gucci', 'Bottega', 'Cartier', 'Cos'], featured: 'Finishing touches' },
];

/** Pick a fitting emoji icon for a sub-category label (keyword match, first wins). */
function subIcon(label: string): string {
  const t = label.toLowerCase();
  const pairs: [RegExp, string][] = [
    [/dress|gown|occasion/, '👗'], [/blouse|cami|tank/, '👚'], [/t-?shirt|tee/, '👕'], [/shirt/, '👔'],
    [/top/, '👚'], [/knit|jumper|sweater|cardigan/, '🧶'], [/coat|jacket|outerwear|trench/, '🧥'],
    [/trouser|chino|pant|slack/, '👖'], [/jean|denim/, '👖'], [/skirt/, '👗'], [/suit|tailor|blazer/, '🤵'],
    [/active|sport|gym/, '🏃'], [/short/, '🩳'], [/baby/, '🍼'], [/toddler/, '🧸'], [/girl/, '👧'],
    [/boy/, '👦'], [/teen/, '🧒'], [/school|uniform/, '🎒'], [/trainer|sneaker/, '👟'], [/boot/, '🥾'],
    [/heel|platform/, '👠'], [/loafer|formal|brogue|oxford/, '🥿'], [/sandal|slider/, '🩴'],
    [/tote|shopper/, '👜'], [/crossbody|shoulder|mini|belt bag/, '👜'], [/clutch|purse/, '👛'],
    [/backpack/, '🎒'], [/travel|luggage|holdall/, '🧳'], [/jewell|necklace|ring|bracelet|earring/, '💍'],
    [/scarf|scarves|wrap/, '🧣'], [/belt/, '🪢'], [/sunglass|glass/, '🕶️'], [/hat|cap/, '🧢'],
    [/wallet/, '👛'], [/hair/, '💇'], [/watch/, '⌚'],
    [/agbada|dashiki|kaftan|abaya|thobe|yoruba|igbo|attire/, '🥻'], [/ankara|print|kente|adire|fabric/, '🧵'],
    [/sari|salwar|lehenga/, '🥻'], [/accessor/, '💍'],
  ];
  for (const [re, ic] of pairs) if (re.test(t)) return ic;
  return '🏷️';
}


export function PublicLayout() {
  const user = useAuthStore((s) => s.user);
  const cartCount = useCart((s) => s.items.length);
  const favCount = useFavourites((s) => s.items.length);
  const [notifOpen, setNotifOpen] = useState(false);

  const { data: overview } = useQuery<{ notifications: { id: string; message: string; date: string; href?: string }[] }>({
    queryKey: ['overview'],
    queryFn: async () => (await api.get('/me/overview')).data,
    enabled: !!user,
    retry: false,
  });
  const notifications = overview?.notifications ?? [];

  // Active members skip the sales page and land on their membership dashboard;
  // guests and non-members still see the pitch.
  const { data: membership } = useQuery<{ tier: string }>({
    queryKey: ['membership'],
    queryFn: async () => (await api.get('/membership')).data,
    enabled: !!user,
    retry: false,
  });
  const isMember = membership?.tier === 'MEMBER';
  const membershipHref = isMember ? '/dashboard/membership' : '/membership';

  const queryClient = useQueryClient();

  /** Opening a notification marks it read, so the bell count drops by one. */
  async function readNotification(id: string) {
    setNotifOpen(false);
    try {
      await api.post(`/me/notifications/${id}/read`);
    } catch { /* non-blocking - navigation still happens */ }
    queryClient.invalidateQueries({ queryKey: ['overview'] });
  }

  async function readAllNotifications() {
    try {
      await api.post('/me/notifications/read-all');
    } catch { /* ignore */ }
    queryClient.invalidateQueries({ queryKey: ['overview'] });
  }

  const [annoIdx, setAnnoIdx] = useState(0);
  const [megaOpen, setMegaOpen] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const megaRef = useRef<HTMLDivElement>(null);
  const megaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const id = setInterval(() => setAnnoIdx((i) => (i + 1) % announcements.length), 4200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (megaRef.current && !megaRef.current.contains(e.target as Node)) setMegaOpen(null);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const anno = announcements[annoIdx];
  const openMega = (slug: string) => { if (megaTimerRef.current) clearTimeout(megaTimerRef.current); setMegaOpen(slug); };
  const closeMega = () => { megaTimerRef.current = setTimeout(() => setMegaOpen(null), 120); };
  const stayMega  = () => { if (megaTimerRef.current) clearTimeout(megaTimerRef.current); };
  const activeMega = megaCategories.find((c) => c.slug === megaOpen);

  return (
    <div className="min-h-screen flex flex-col bg-cream has-bottom-nav">

      {/* Announcement bar - fixed height + single line. A variable-height bar
          pinned above everything is a layout-jump bug: when the cycling
          announcements differ in length, some wrap to two lines and the bar
          grows/shrinks every 4.2s, shoving the whole page up and down. A fixed
          height with non-wrapping, truncating text keeps it perfectly still. */}
      <div className="section-dark bg-ink text-cream/80 text-[11px] h-9 flex items-center justify-center text-center tracking-wider border-b border-ink-700 overflow-hidden px-4">
        <div key={annoIdx} className="animate-fade-up flex items-center justify-center gap-2 whitespace-nowrap min-w-0">
          <span className="shrink-0">{anno.icon}</span>
          <span className="mono truncate">{anno.text}</span>
          {anno.code && (
            <span className={`mono font-semibold px-2 py-0.5 rounded shrink-0 ${anno.codeLabel ? 'text-gold bg-gold/10' : 'text-gold'}`}>
              {anno.code}
            </span>
          )}
        </div>
      </div>

      {/* Sticky header */}
      <header className="border-b border-warmline bg-cream/97 backdrop-blur sticky top-0 z-40" ref={megaRef}>
        <div className="max-w-[1800px] mx-auto px-4 lg:px-6 h-[72px] flex items-center justify-between gap-4">

          <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="Souqli - home">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-ink shadow-glow overflow-hidden">
              <img src="/souqli-mark.png" alt="" className="w-[26px] h-[26px] object-contain" />
            </div>
            <div className="block">
              <div className="display text-ink text-xl leading-none">Souqli</div>
            </div>
          </Link>

          {/* Desktop nav - centred */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-0.5">
            {megaCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/c/${cat.slug}`}
                onMouseEnter={() => openMega(cat.slug)}
                onMouseLeave={closeMega}
                onClick={() => setMegaOpen(null)}
                className={`px-3 py-2 text-[13px] rounded-luxe transition-all relative
                  ${megaOpen === cat.slug ? 'text-ink bg-cream-deep font-medium' : 'text-mute hover:text-ink hover:bg-cream-deep'}`}>
                {cat.label}
                {megaOpen === cat.slug && <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-gold rounded-full" />}
              </Link>
            ))}
            <NavLink to={membershipHref} className={({ isActive }) =>
              `px-3 py-2 text-[13px] rounded-luxe transition-all inline-flex items-center gap-1.5 ${isActive ? 'text-ink bg-gold/10 text-gold-dark font-medium' : 'text-mute hover:text-ink hover:bg-cream-deep'}`}>
              Membership
              {isMember && <span className="w-1.5 h-1.5 rounded-full bg-gold" title="Active member" />}
            </NavLink>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            <Link to="/saved" aria-label="Saved items"
              className="hidden lg:flex w-9 h-9 rounded-full items-center justify-center text-mute hover:text-ink hover:bg-cream-deep transition relative">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 21s-7-4.5-9.3-9.2C1 8.4 3.4 5 6.7 5c2 0 3.4 1.1 4.3 2.5C12 6.1 13.4 5 15.4 5c3.3 0 5.7 3.4 4 6.8C19 16.5 12 21 12 21z"/>
              </svg>
              {favCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-gold text-ink text-[10px] font-bold rounded-full grid place-items-center">{favCount}</span>
              )}
            </Link>
            {user && (
              <div className="relative hidden lg:block">
                <button aria-label="Notifications" onClick={() => setNotifOpen((v) => !v)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-mute hover:text-ink hover:bg-cream-deep transition relative">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  {notifications.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-bad text-cream text-[10px] font-bold rounded-full grid place-items-center">{notifications.length}</span>
                  )}
                </button>
                {notifOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-luxe border border-warmline shadow-frame z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-warmline flex items-center justify-between">
                        <span className="font-semibold text-sm">Notifications</span>
                        {notifications.length > 0 && (
                          <button onClick={readAllNotifications} className="text-[11px] text-gold-dark hover:underline">Mark all read</button>
                        )}
                      </div>
                      {notifications.length ? (
                        <ul className="max-h-80 overflow-y-auto divide-y divide-warmline/50">
                          {notifications.map((n) => (
                            <li key={n.id}>
                              <Link to={n.href ?? '/dashboard'} onClick={() => readNotification(n.id)} className="block px-4 py-3 hover:bg-cream/50">
                                <div className="text-xs text-ink leading-snug">{n.message}</div>
                                <div className="text-[10px] text-mute mt-1">{new Date(n.date).toLocaleDateString()}</div>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="px-4 py-8 text-center text-mute text-xs">You're all caught up.</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <ThemeToggle />
            <Link to="/cart" className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-cream-deep transition" aria-label="Bag">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-gold text-ink text-[10px] font-bold rounded-full grid place-items-center">{cartCount}</span>
              )}
            </Link>
            {user ? (
              <Link to="/dashboard" className="btn btn-outline btn-sm hidden lg:inline-flex">My Souqli</Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm hidden sm:inline-flex">Log in</Link>
                <Link to="/register" className="btn btn-gold btn-sm">Join free</Link>
              </>
            )}
            <Link to="/dashboard/sell" className="btn btn-primary btn-sm hidden lg:inline-flex">+ Sell</Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center hover:bg-cream-deep transition"
              aria-label="Menu">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {mobileMenuOpen
                  ? <><path d="M18 6 6 18"/><path d="M6 6l12 12"/></>
                  : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
              </svg>
            </button>
          </div>
        </div>

        <div className="gold-rule" />

        {/* Mega-menu - Shein-style: department rail + dense link columns */}
        {activeMega && (
          <div
            className="absolute left-0 right-0 top-full z-50 animate-in slide-in-from-top-1 duration-150"
            onMouseEnter={stayMega}
            onMouseLeave={closeMega}
          >
            <div className="bg-white border-b border-warmline shadow-frame">
              <div className="max-w-[1800px] mx-auto grid grid-cols-[200px_1fr_240px]">

                {/* Left department rail */}
                <div className="bg-cream-deep/40 border-r border-warmline py-3">
                  <div className="stamp text-mute text-[9px] px-5 mb-1.5 uppercase tracking-widest">Explore category</div>
                  {megaCategories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/c/${c.slug}`}
                      onMouseEnter={() => openMega(c.slug)}
                      onClick={() => setMegaOpen(null)}
                      className={`flex items-center justify-between gap-2 px-5 py-2.5 text-[13px] border-l-[3px] transition-colors
                        ${c.slug === activeMega.slug ? 'border-gold bg-white text-ink font-semibold' : 'border-transparent text-mute hover:text-ink hover:bg-white/70'}`}
                    >
                      <span className="flex items-center gap-2"><span className="text-base">{c.icon}</span>{c.label}</span>
                      <span className={`text-gold ${c.slug === activeMega.slug ? 'opacity-100' : 'opacity-0'}`}>&rsaquo;</span>
                    </Link>
                  ))}
                </div>

                {/* Middle - round category chips + dense link columns */}
                <div className="px-8 py-6">
                  {/* round category chips (Shein-style) */}
                  <div className="flex flex-wrap gap-x-6 gap-y-3 pb-5 mb-5 border-b border-warmline">
                    {activeMega.subs.slice(0, 6).map((sub) => (
                      <Link
                        key={sub}
                        to={`/c/${activeMega.slug}?sub=${encodeURIComponent(sub)}`}
                        onClick={() => setMegaOpen(null)}
                        className="group flex flex-col items-center gap-1.5 w-[60px] text-center"
                      >
                        <span
                          className="w-14 h-14 rounded-full grid place-items-center text-xl shadow-luxe border border-warmline/60 group-hover:-translate-y-0.5 group-hover:shadow-frame transition-all"
                          style={{ background: 'radial-gradient(circle at 30% 28%, #FBF3E4, #F0D9A8 60%, #D98A2B)' }}
                        >
                          {subIcon(sub)}
                        </span>
                        <span className="text-[10px] text-mute group-hover:text-ink leading-tight">{sub.split(' ')[0]}</span>
                      </Link>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-x-8">
                    {/* Subcategories across two columns */}
                    <div className="col-span-2">
                      <div className="text-ink text-[11px] mb-1.5 font-semibold uppercase tracking-widest">{activeMega.label} categories</div>
                      <div className="grid grid-cols-2 gap-x-6">
                        {activeMega.subs.map((sub) => (
                          <Link
                            key={sub}
                            to={`/c/${activeMega.slug}?sub=${encodeURIComponent(sub)}`}
                            onClick={() => setMegaOpen(null)}
                            className="flex items-center gap-2 py-[7px] text-[13px] text-mute hover:text-gold-dark hover:translate-x-0.5 transition-all"
                          >
                            <span className="w-4 text-center text-[13px] shrink-0">{subIcon(sub)}</span>
                            <span>{sub}</span>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Condition + brands column */}
                    <div>
                      <div className="text-ink text-[11px] mb-1.5 font-semibold uppercase tracking-widest">By condition</div>
                      {['Never worn', 'Worn once', 'Like new', 'Well loved'].map((c) => (
                        <Link
                          key={c}
                          to={`/c/${activeMega.slug}?condition=${encodeURIComponent(c)}`}
                          onClick={() => setMegaOpen(null)}
                          className="block py-[7px] text-[13px] text-mute hover:text-gold-dark transition-colors"
                        >
                          {c}
                        </Link>
                      ))}
                      <div className="text-ink text-[11px] mt-4 mb-2 font-semibold uppercase tracking-widest">Top brands</div>
                      <div className="flex flex-wrap gap-1.5">
                        {activeMega.brands.map((b) => (
                          <Link
                            key={b}
                            to={`/c/${activeMega.slug}?brand=${encodeURIComponent(b)}`}
                            onClick={() => setMegaOpen(null)}
                            className="px-2.5 py-1 text-[11px] border border-warmline rounded-full text-mute hover:border-gold hover:text-ink transition-all"
                          >
                            {b}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right - single promo image */}
                <div className="border-l border-warmline p-4 flex flex-col">
                  <Link
                    to={`/c/${activeMega.slug}?sort=new`}
                    onClick={() => setMegaOpen(null)}
                    className="group block rounded-luxe overflow-hidden relative h-[190px] shadow-luxe"
                  >
                    <img
                      src={activeMega.img}
                      alt=""
                      aria-hidden
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      {activeMega.featured && <span className="pill bg-gold text-ink text-[9px]">{activeMega.featured}</span>}
                      <div className="display text-cream text-lg mt-1 leading-tight">New in {activeMega.label}</div>
                      <span className="inline-flex items-center gap-1 mt-1 text-gold text-[12px] font-medium group-hover:gap-2 transition-all">Shop now &rarr;</span>
                    </div>
                  </Link>
                  <Link
                    to={`/c/${activeMega.slug}`}
                    onClick={() => setMegaOpen(null)}
                    className="block text-center mt-3 btn btn-outline btn-sm text-[12px]"
                  >
                    View all {activeMega.label}
                  </Link>
                  <Link
                    to="/dashboard/sell"
                    onClick={() => setMegaOpen(null)}
                    className="block text-center mt-2 text-[11px] text-mute hover:text-ink underline underline-offset-2"
                  >
                    Sell your {activeMega.label.toLowerCase()} &rarr;
                  </Link>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-cream border-b border-warmline shadow-frame">
            <div className="px-4 py-4 space-y-1">
              {megaCategories.map((cat) => (
                <Link key={cat.slug} to={`/c/${cat.slug}`} onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between px-3 py-3 rounded-luxe hover:bg-cream-deep text-sm text-ink">
                  {cat.label}
                  <svg className="w-4 h-4 text-mute" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </Link>
              ))}
              <Link to={membershipHref} onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-luxe hover:bg-cream-deep text-sm text-ink">
                <span className="inline-flex items-center gap-1.5">
                  Membership
                  {isMember && <span className="w-1.5 h-1.5 rounded-full bg-gold" title="Active member" />}
                </span>
                <svg className="w-4 h-4 text-mute" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </Link>
              <div className="pt-3 border-t border-warmline flex gap-2">
                {user ? (
                  <Link to="/dashboard" className="btn btn-outline btn-sm flex-1 text-center" onClick={() => setMobileMenuOpen(false)}>My Souqli</Link>
                ) : (
                  <>
                    <Link to="/login" className="btn btn-outline btn-sm flex-1 text-center" onClick={() => setMobileMenuOpen(false)}>Log in</Link>
                    <Link to="/register" className="btn btn-gold btn-sm flex-1 text-center" onClick={() => setMobileMenuOpen(false)}>Join free</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <BottomNav />

      {/* Footer */}
      <footer className="section-dark border-t border-warmline mt-20 bg-ink text-cream relative overflow-hidden">
        <div className="absolute inset-0 dotgrid-light bg-[length:24px_24px] opacity-30" />
        <div className="relative max-w-[1800px] mx-auto px-6 pt-16 pb-10">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <img src="/souqli-logo.png" alt="Souqli" className="h-11 w-auto" />
              <p className="mt-4 text-mute-soft text-sm leading-relaxed max-w-[36ch]">
                The curated recommerce marketplace for pre-loved fashion, tech, and more. Every seller verified. Every payment secured by Stripe.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                {['🔒 Stripe Secured', '✅ ID Verified', '📦 Locker Delivery'].map((b) => (
                  <span key={b} className="pill pill-gold text-[10px]">{b}</span>
                ))}
              </div>
              <form className="mt-6 flex gap-2 max-w-sm">
                <input type="email" placeholder="hello@example.com" className="input bg-ink-800 border-ink-600 text-cream placeholder:text-mute" />
                <button className="btn btn-gold btn-sm shrink-0">Subscribe</button>
              </form>
              <div className="stamp text-mute mt-3 text-[9px]">Weekly drops · Unsubscribe anytime</div>
            </div>
            <FooterCol title="Marketplace"    links={[['Shop all', '/c/all'], ['Flash Drops', '/c/drops'], ['Women', '/c/womenswear'], ['Men', '/c/menswear'], ['Electronics', '/c/electronics'], ['Free shelf', '/free']]} />
            <FooterCol title="Sell & Earn"    links={[['Start selling', '/dashboard/sell'], ['AI listing tool', '/ai-listing'], ['Swap a device', '/dashboard/swap'], ['Instant payout', '/dashboard/wallet'], ['Seller fees', '/trust']]} />
            <FooterCol title="Trust & Safety" links={[['How it works', '/trust'], ['Payments & payouts', '/trust'], ['Refund Policy & Buyer Protection', '/refund-policy'], ['Privacy & data protection', '/privacy'], ['Dispute resolution', '/disputes'], ['Stolen goods check', '/stolen-goods']]} />
            <FooterCol title="Company"        links={[['Help Centre', '/help'], ['Contact support', '/contact'], ['About Souqli', '/about'], ['Membership', membershipHref]]} />
          </div>
          <div className="mt-12 pt-6 border-t border-ink-700 flex flex-col md:flex-row md:items-center justify-between gap-4 text-mute-soft text-xs">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>&copy; {new Date().getFullYear()} Souqli - All rights reserved</span>
              <span className="hidden md:inline text-mute">&middot;</span>
              <span className="flex items-center gap-2">
                <span className="stamp text-mute text-[9px]">Designed &amp; built by</span>
                <a href="https://www.synterra.co.uk" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center transition hover:opacity-90" aria-label="Synterra Technologies">
                  <img src={synterraLogo} alt="Synterra Technologies" className="h-7 w-auto" />
                </a>
              </span>
            </div>
            <div className="flex gap-6">
              <Link to="/terms" className="hover:text-cream">Terms</Link>
              <Link to="/privacy" className="hover:text-cream">Privacy</Link>
              <Link to="/cookies" className="hover:text-cream">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div className="md:col-span-2">
      <div className="stamp text-gold mb-4 text-[10px]">{title}</div>
      <ul className="space-y-2.5 text-sm text-mute-soft">
        {links.map(([label, href]) => (
          <li key={href}><Link to={href} className="hover:text-cream transition">{label}</Link></li>
        ))}
      </ul>
       </div>
  );
}
